/**
 * ONE APP PER DOCUMENT.
 *
 * A web recreate renames the hashed lazy chunks, so a page that is mid-session at the deploy gets
 * a 404 on its next `import()`. `game/card-faces.ts` then reads `/.vite/manifest.json` and retries
 * the NEW chunk — whose first statement is `import … from "./index-<newhash>.js"`. Browsers key
 * modules by URL, so that pulls a SECOND, fresh instance of the entry chunk into a document that
 * is already running the old one, and everything `main.tsx` does at module scope runs AGAIN —
 * including `createRoot(#root).render()`. Two React roots then reconcile the same children until
 * one removes a node the other has already moved:
 *
 *   NotFoundError: Failed to execute 'removeChild' on 'Node'
 *
 * Traced on prod twice — 2026-08-08 (iOS) and 2026-08-16 (Android) — with the identical request
 * chain (404 → manifest → `?retry=1` chunk → new `index-*.js` → a second `/api/me` boot with NO
 * `GET /` → crash beacon), and it is the cause of every `render-crash removeChild` row we have.
 *
 * ⚠️ THE MARKER MUST LIVE ON THE GLOBAL, NOT IN MODULE SCOPE. The two instances have SEPARATE
 * module scopes — a plain `let` here would be `false` in the second one and guard nothing. It also
 * must NOT go into localStorage/sessionStorage: that would survive a real reload and refuse to
 * boot the app ever again.
 *
 * The key is a plain string on purpose, so a deploy can be verified by grepping the served bundle.
 */
const CLAIM_KEY = "__bingAppBootstrapClaimed";

/**
 * Claim the right to bootstrap the app. `true` for the first caller in this document, `false` for
 * every later one — so a second entry-chunk instance loads its modules and then does nothing.
 */
export function claimAppBootstrap(): boolean {
  const globals = globalThis as unknown as Record<string, unknown>;
  if (globals[CLAIM_KEY] === true) return false;
  globals[CLAIM_KEY] = true;
  return true;
}
